import { Link } from '@tanstack/react-router';
import type { CityDto, SourceChannelDto } from '@upme/contracts';
import { bcp47, resolveLocale } from '@upme/i18n';
import { Badge } from '@upme/ui/components/badge';
import { Button } from '@upme/ui/components/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@upme/ui/components/table';
import { Loader2, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { EmptyState } from '../../../shared/components/empty-state';
import { useFetchSourceChannelNow } from '../mutations';

interface Props {
  channels: ReadonlyArray<SourceChannelDto>;
  cities: ReadonlyArray<CityDto>;
}

const dateTimeFmtCache = new Map<string, Intl.DateTimeFormat>();

function getDateTimeFmt(locale: string): Intl.DateTimeFormat {
  const bcpLocale = bcp47(resolveLocale(locale));
  let fmt = dateTimeFmtCache.get(bcpLocale);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat(bcpLocale, { dateStyle: 'short', timeStyle: 'short' });
    dateTimeFmtCache.set(bcpLocale, fmt);
  }
  return fmt;
}

function FetchNowButton({ id }: { id: string }) {
  const { t } = useTranslation('source-channels');
  const mutation = useFetchSourceChannelNow(id);
  const onClick = async () => {
    try {
      await mutation.mutateAsync();
      toast.success(t('table.fetchNowSuccess'));
    } catch (err) {
      const message = err instanceof Error ? err.message : t('table.fetchNowError');
      toast.error(message);
    }
  };
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={onClick}
      disabled={mutation.isPending}
      title={t('table.fetchNowTitle')}
    >
      {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
    </Button>
  );
}

export function SourceChannelsTable({ channels, cities }: Props) {
  const { t, i18n } = useTranslation('source-channels');

  if (channels.length === 0) {
    return (
      <EmptyState
        title={t('table.emptyTitle')}
        description={t('table.emptyDescription')}
        cta={
          <Button asChild>
            <Link to="/source-channels/new">{t('table.addChannel')}</Link>
          </Button>
        }
      />
    );
  }

  const cityById = new Map(cities.map((c) => [c.id, c]));
  const dateFmt = getDateTimeFmt(i18n.language);

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t('table.headerSlug')}</TableHead>
          <TableHead>{t('table.headerTitle')}</TableHead>
          <TableHead>{t('table.headerCity')}</TableHead>
          <TableHead>{t('table.headerLastFetch')}</TableHead>
          <TableHead>{t('table.headerErrors')}</TableHead>
          <TableHead>{t('table.headerMessages')}</TableHead>
          <TableHead>{t('table.headerKind')}</TableHead>
          <TableHead>{t('table.headerStatus')}</TableHead>
          <TableHead className="text-right">{t('table.headerActions')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {channels.map((c) => {
          const city = cityById.get(c.cityId);
          return (
            <TableRow key={c.id}>
              <TableCell className="font-mono text-xs">
                <a
                  href={
                    c.topicId > 0
                      ? `https://t.me/${c.slug}/${c.topicId}`
                      : // typeof, а не `!== null`: DTO рантаймом не парсится, от старого
                        // API поле придёт undefined и дало бы ссылку на .../undefined.
                        typeof c.walkCursor === 'number'
                        ? // Ведём на сообщение, где остановился перебор — видно, докуда дошли.
                          `https://t.me/${c.slug}/${c.walkCursor}`
                        : `https://t.me/${c.slug}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline"
                >
                  @{c.slug}
                  {c.topicId > 0 ? `/${c.topicId}` : ''}
                  {typeof c.walkCursor === 'number' ? ` ↦${c.walkCursor}` : ''}
                </a>
              </TableCell>
              <TableCell>
                <Link
                  to="/source-channels/$channelId"
                  params={{ channelId: c.id }}
                  className="hover:underline"
                >
                  {c.title}
                </Link>
              </TableCell>
              <TableCell>{city?.name ?? '—'}</TableCell>
              <TableCell className="text-sm">
                {c.lastFetchedAt ? dateFmt.format(new Date(c.lastFetchedAt)) : '—'}
              </TableCell>
              <TableCell>
                {c.consecutiveErrors > 0 ? <Badge variant="destructive">{c.consecutiveErrors}</Badge> : null}
              </TableCell>
              <TableCell>{c.messagesCount}</TableCell>
              <TableCell>
                {c.kind === 'NEWS' ? (
                  <Badge variant="secondary">{t('kind.news')}</Badge>
                ) : (
                  <Badge>{t('kind.events')}</Badge>
                )}
              </TableCell>
              <TableCell>
                {c.isActive ? (
                  <Badge variant="default">{t('table.statusActive')}</Badge>
                ) : (
                  <Badge variant="secondary">{t('table.statusDisabled')}</Badge>
                )}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-1">
                  <Button asChild variant="ghost" size="sm">
                    <Link to="/source-channels/$channelId" params={{ channelId: c.id }}>
                      {t('table.open')}
                    </Link>
                  </Button>
                  <FetchNowButton id={c.id} />
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
